import { useReducer, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

interface AssessmentState {
  user_id: string;
  current_step: number;
  answers: Record<string, any>;
  assessment_type: string;
  completed: boolean;
  last_completed_step: number;
  created_at?: string;
  updated_at?: string;
  error?: string | null;
  ai_analysis?: {
    pending: boolean;
    results?: any;
  };
}

type AssessmentAction =
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'SET_RESPONSE'; payload: { question: string; value: any } }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'LOAD_PROGRESS'; payload: AssessmentState }
  | { type: 'COMPLETE_ASSESSMENT' }
  | { type: 'SET_AI_ANALYSIS'; payload: any }

const initialState: AssessmentState = {
  user_id: '',
  current_step: 0,
  answers: {},
  assessment_type: '',
  completed: false,
  last_completed_step: -1,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
}

function assessmentReducer(state: AssessmentState, action: AssessmentAction): AssessmentState {
  switch (action.type) {
    case 'NEXT_STEP':
      return {
        ...state,
        current_step: state.current_step + 1,
        last_completed_step: Math.max(state.last_completed_step, state.current_step),
        updated_at: new Date().toISOString()
      }
    case 'PREV_STEP':
      return {
        ...state,
        current_step: Math.max(0, state.current_step - 1),
        updated_at: new Date().toISOString()
      }
    case 'SET_RESPONSE':
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.payload.question]: action.payload.value,
        },
        updated_at: new Date().toISOString()
      }
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload
      }
    case 'LOAD_PROGRESS':
      return {
        ...state,
        ...action.payload
      }
    case 'COMPLETE_ASSESSMENT':
      return {
        ...state,
        completed: true,
        updated_at: new Date().toISOString()
      }
    case 'SET_AI_ANALYSIS':
      return {
        ...state,
        ai_analysis: {
          pending: false,
          results: action.payload
        }
      }
    default:
      return state
  }
}

export function useAssessmentReducer(assessmentType: string) {
  const [state, dispatch] = useReducer(assessmentReducer, {
    ...initialState,
    assessment_type: assessmentType,
    ai_analysis: { pending: false }
  })

  const saveProgress = useCallback(async (userId?: string) => {
    if (!userId) return

    try {
      const updates = {
        user_id: userId,
        assessment_type: assessmentType,
        current_step: state.current_step,
        answers: state.answers,
        completed: state.completed,
        last_completed_step: state.last_completed_step,
        updated_at: new Date().toISOString()
      }

      const { error } = await supabase
        .from('assessment_states')
        .upsert(updates)
        .eq('user_id', userId)
        .eq('assessment_type', assessmentType)

      if (error) throw error
    } catch (error) {
      console.error('Error saving assessment progress:', error)
      throw error
    }
  }, [state, assessmentType])

  const loadSavedProgress = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('assessment_states')
        .select('*')
        .eq('user_id', userId)
        .eq('assessment_type', assessmentType)
        .single()

      if (error && error.code !== 'PGRST116') throw error

      if (data) {
        dispatch({
          type: 'LOAD_PROGRESS',
          payload: data
        })
      }
    } catch (error) {
      console.error('Error loading assessment progress:', error)
    }
  }, [assessmentType])

  const handleResponse = useCallback((question: string, value: any) => {
    dispatch({
      type: 'SET_RESPONSE',
      payload: { question, value },
    })
  }, [])

  const isValid = useCallback((step: number) => {
    // Add validation logic here based on required fields for each step
    return true
  }, [])

  const completeAssessment = useCallback(async (userId: string) => {
    if (!userId) return;
    
    try {
      // Save final state
      await saveProgress(userId);

      // Save to assessment_responses
      const { error: responseError } = await supabase
        .from('assessment_responses')
        .insert({
          user_id: userId,
          assessment_type: assessmentType,
          responses: state.answers,
          completed_at: new Date().toISOString()
        });

      if (responseError) throw responseError;

      // Get reward amount based on assessment type
      const rewards = {
        initial: 100,
        skills: 75,
        personality: 50
      };
      const coinReward = rewards[assessmentType as keyof typeof rewards] || 50;

      // Update user profile
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({
          [`assessments.${assessmentType}`]: true,
          [`assessments.${assessmentType}_completed_at`]: new Date().toISOString(),
          career_coins: supabase.sql`COALESCE(career_coins, 0) + ${coinReward}`,
          profile_completion: {
            percentage: 25,
            completed_sections: ['initial_assessment']
          }
        })
        .eq('user_id', userId);

      if (profileError) throw profileError;

      // Record coin transaction
      await supabase
        .from('coin_transactions')
        .insert({
          user_id: userId,
          amount: coinReward,
          type: 'earn',
          description: `Completed ${assessmentType} Assessment`,
          source: 'assessment'
        });

      dispatch({ type: 'COMPLETE_ASSESSMENT' });

    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: 'Failed to complete assessment' 
      });
      throw error;
    }
  }, [state, assessmentType, saveProgress]);

  return {
    state,
    dispatch,
    saveProgress,
    loadSavedProgress,
    handleResponse,
    isValid,
    completeAssessment
  }
} 